import { Button, confirm } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  DownloadIcon,
  EditIcon,
  HelpCircle,
  TrashIcon,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDeleteQCRecord, useQCRecordQuery } from "../api/queries";
import {
  QCRecordDetailTour,
  useQCRecordDetailTour,
} from "../tour/QCRecordDetailTour";
import { QCRecordDetailContent } from "../ui/QCRecordDetailContent";

export function QCRecordDetailPage() {
  const { recordId } = useParams({ from: "/app/qc/records/$recordId" });
  const nav = useNavigate({ from: "/app/qc/records/$recordId" });
  const tour = useQCRecordDetailTour();
  const canEdit = useActionPermission(["qc_record:update"]);
  const canDelete = useActionPermission(["qc_record:delete"]);

  const { data: record, isLoading, error } = useQCRecordQuery(recordId);
  const deleteMutation = useDeleteQCRecord();

  const handleEdit = () => {
    if (record) {
      nav({
        to: "/app/qc/$id/record/$recordId",
        params: { id: record.qcStandardId, recordId: record.id },
      });
    }
  };

  const handleDelete = async () => {
    if (!record) return;

    const ok = await confirm({
      title: "ລຶບບັນທຶກ QC",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບບັນທຶກ QC ນີ້? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: {
        variant: "destructive",
      },
    });

    if (ok) {
      deleteMutation.mutate(record.id, {
        onSuccess: () => {
          nav({ to: "/app/qc/records" });
        },
      });
    }
  };

  const handleBack = () => {
    nav({ to: "/app/qc/records" });
  };

  const handleDownloadPDF = () => {
    if (record) {
      nav({
        to: "/app/qc/records/$recordId/pdf-preview",
        params: { recordId: record.id },
      });
    }
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !record) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບບັນທຶກ QC
              </h2>
              <p className="mb-4 text-muted-foreground">
                ບັນທຶກ QC ທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button onClick={handleBack}>
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບໄປທີ່ລາຍການ
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />

      <Main>
        <QCRecordDetailTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />

        <div className="space-y-4">
          {/* Header Actions */}
          <div
            className="mb-6 flex items-center justify-between"
            data-tourid="record-detail-header"
          >
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-semibold text-2xl">ລາຍລະອຽດບັນທຶກ QC</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground text-sm">
                ຂໍ້ມູນລາຍລະອຽດຂອງບັນທຶກ QC
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={handleBack} className="gap-2">
                <ArrowLeftIcon className="h-4 w-4" />
                ກັບ
              </Button>
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              className="gap-2"
            >
              <DownloadIcon className="h-4 w-4" />
              ເບິ່ງ PDF Preview
            </Button>
            {canEdit && (
              <Button onClick={handleEdit} className="gap-2">
                <EditIcon className="h-4 w-4" />
                ແກ້ໄຂ
              </Button>
            )}
            {canDelete && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="gap-2"
                disabled={deleteMutation.isPending}
              >
                <TrashIcon className="h-4 w-4" />
                {deleteMutation.isPending ? "ກຳລັງລຶບ..." : "ລຶບ"}
              </Button>
            )}
            </div>
          </div>

          {/* Record Content */}
          <QCRecordDetailContent record={record} />
        </div>
      </Main>
    </>
  );
}
